<template>
  <ComponentState :fetch-status="[industryGroupItemsStatus, symbolStatus]">
    <template #loading>
      <TableSkeleton :row-number="15" :column-number="10" />
    </template>

    <template #error>
      <Error @retry="retryIndustryGroups" />
    </template>

    <NTable
      v-if="industryGroupsItems.length"
      :data="industryGroupsItems"
      :column-splitter="false"
      :striped="false"
      cell-padding="small"
      :sort-by="{
        prop: 'name',
        order: 'ascending',
      }"
      :highlight-by="{
        prop: 'isin',
        value: activeIsin,
      }"
      fixed-header
      class="m-2 max-h-full"
      hover-alignment="right"
      hover-offset="10%"
      @row-click="(row) => updateActiveIsin(row.isin)"
    >
      <template #hoveredElements="{ row }">
        <NButton
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'sameGroup',
            component: 'buyButton',
          }"
          size="mini"
          variant="fill"
          @click.stop="handleBuy(row)"
        >
          <NText variant="overline" weight="medium">
            {{ t('common.buy') }}
          </NText>
        </NButton>

        <NButton
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'sameGroup',
            component: 'sellButton',
          }"
          size="mini"
          color="red"
          :disabled="offersIsin?.includes(row.isin)"
          variant="fill"
          class="mr-1"
          @click.stop="
            openNewOrderFormDialog({
              type: ORDER_TYPES.sell,
              symbol: {
                instrumentName: row.name as string,
                isin: getSymbolIsin(row),
              },
            })
          "
        >
          <NText variant="overline" weight="medium">
            {{ t('common.sell') }}
          </NText>
        </NButton>
      </template>

      <NTableColumn
        v-slot="{ row }"
        :label="t('common.symbol')"
        prop="name"
        class="w-28"
        sortable
        align="text-right"
      >
        <span :key="row.name" class="inline-flex justify-center items-center">
          <SymbolTradeStateIcon
            class="me-1"
            :trading-state-code="row.instrumentTradingState"
          />
          <NText variant="caption2" weight="medium">
            {{ row.name }}
          </NText>
        </span>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('symbol.current')"
        prop="referencePriceVariationPercentage"
        class="w-28"
        sortable
        align="text-right"
      >
        <div
          :key="`${row.isin}-${row.lastTradePrice}-${row.referencePriceVariationPercentage}`"
          class="flex items-center"
        >
          <NumberFormatter
            #="{ number }"
            :value="row.referencePriceVariationPercentage / 100"
            number-style="percent"
            maximum-fraction-digits="2"
          >
            <ChangePriceEffect :price="row.referencePriceVariationPercentage">
              <NText
                dir="ltr"
                variant="caption2"
                weight="medium"
                :class="{
                  'text-brand-primary':
                    row.referencePriceVariationPercentage > 0,
                  'text-critical-primary':
                    row.referencePriceVariationPercentage < 0,
                }"
                class="ms-1"
              >
                ({{ number }})
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="row.lastTradePrice">
            <ChangePriceEffect :price="row.lastTradePrice">
              <NText
                :key="row.lastTradePrice"
                variant="caption2"
                weight="medium"
              >
                {{ number }}
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>
        </div>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('misc.final')"
        prop="closingPriceVariationPercentage"
        sortable
        align="text-right"
      >
        <div
          :key="`${row.isin}-${row.closingPrice}-${row.closingPriceVariationPercentage}`"
          class="flex items-center"
        >
          <NumberFormatter
            #="{ number }"
            :value="row.closingPriceVariationPercentage / 100"
            number-style="percent"
            maximum-fraction-digits="2"
          >
            <ChangePriceEffect :price="row.closingPriceVariationPercentage">
              <NText
                dir="ltr"
                variant="caption2"
                weight="medium"
                :class="{
                  'text-brand-primary': row.closingPriceVariationPercentage > 0,
                  'text-critical-primary':
                    row.closingPriceVariationPercentage < 0,
                }"
                class="ms-1"
              >
                ({{ number }})
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="row.closingPrice">
            <ChangePriceEffect :price="row.closingPrice">
              <NText :key="row.closingPrice" variant="caption2" weight="medium">
                {{ number }}
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>
        </div>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('symbol.buyّIndividualAverage')"
        prop="individualBuyDensity"
        sortable
        align="text-right"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.individualBuyDensity"
          notation="compact"
          maximum-fraction-digits="2"
        >
          <NText
            :key="`${row.isin}-${row.individualBuyDensity}`"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('symbol.sellIndividualAverage')"
        prop="individualSellDensity"
        sortable
        align="text-right"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.individualSellDensity"
          notation="compact"
          maximum-fraction-digits="2"
        >
          <NText
            :key="`${row.isin}-${row.individualSellDensity}`"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('symbol.incomingFirmMoney')"
        prop="incomingIndividualMoney"
        sortable
        align="text-right"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.incomingIndividualMoney"
          notation="compact"
          maximum-fraction-digits="2"
        >
          <NText
            :key="`${row.isin}-${row.incomingIndividualMoney}`"
            dir="ltr"
            variant="caption2"
            weight="medium"
            :class="{
              'text-brand-primary': row.incomingIndividualMoney > 0,
              'text-critical-primary': row.incomingIndividualMoney < 0,
            }"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('symbol.buyّIndividualVolume')"
        prop="individualBuyVolume"
        sortable
        align="text-right"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.individualBuyVolume"
          notation="compact"
          maximum-fraction-digits="2"
        >
          <NText variant="caption2" weight="medium">
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('symbol.sellّIndividualVolume')"
        prop="individualSellVolume"
        sortable
        align="text-right"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.individualSellVolume"
          notation="compact"
          maximum-fraction-digits="2"
        >
          <NText
            :key="`${row.isin}-${row.individualSellVolume}`"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('symbol.buyFirmVolume')"
        prop="firmBuyVolume"
        sortable
        align="text-right"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.firmBuyVolume"
          notation="compact"
          maximum-fraction-digits="2"
        >
          <NText
            :key="`${row.isin}-${row.firmBuyVolume}`"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('symbol.sellFirmVolume')"
        prop="firmSellVolume"
        sortable
        align="text-right"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.firmSellVolume"
          notation="compact"
          maximum-fraction-digits="2"
        >
          <NText
            :key="`${row.isin}-${row.firmSellVolume}`"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>
    </NTable>

    <EmptyList
      v-else
      :title="t('common.noInformationAvailable')"
      class="h-full flex flex-col justify-center items-center"
      title-class="text-center"
    >
      <template #image>
        <Image src="empty_box" alt="not_found" class="w-28 h-max" />
      </template>
    </EmptyList>
  </ComponentState>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { InstrumentSectorDetail } from '@libs/api-reference';
import { ORDER_TYPES } from '@core/order';
import {
  getSymbolIsin,
  INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  FUNDS_SECTOR_CODES,
  useLiveIndustryGroupsQuery,
} from '@core/symbol';
import { isEmpty } from '@libs/utils';
import { useLiveInitialPublicOffers } from '@core/ipo';

import {
  SymbolTradeStateIcon,
  useActiveSymbolIsin,
  useLiveSymbolQuery,
} from '@/modules/symbol';
import { useOrderDialogs } from '@/modules/order';
import { useIpoDialogs } from '@/modules/ipo';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import EmptyList from '@/components/EmptyList.vue';
import Error from '@/components/Error.vue';
import Image from '@/components/Image.vue';
import ComponentState from '@/components/ComponentState.vue';
import TableSkeleton from '@/components/TableSkeleton.vue';

const props = defineProps<{
  isin: string;
}>();

const { t } = useI18n();

const { activeIsin, updateActiveIsin } = useActiveSymbolIsin();

const { symbol, isErrorSymbol, getSymbol, symbolStatus } = useLiveSymbolQuery(
  toRef(props, 'isin')
);

const { openNewOrderFormDialog } = useOrderDialogs();
const { openBuyOfferDialog } = useIpoDialogs();

const options = computed(() => {
  if (
    symbol.value?.sectorCode &&
    FUNDS_SECTOR_CODES.includes(symbol.value?.sectorCode?.trim())
  ) {
    return {
      sectorCodes: symbol.value.sectorCode.trim(),
      subSectorCodes: symbol.value.subSectorCode,
      types: symbol.value?.instrumentTypeCode || '',
    };
  }

  return {
    sectorCodes: (symbol.value?.sectorCode as string)?.trim(),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  };
});

const { industryGroupsItems, getIndustryGroupItems, industryGroupItemsStatus } =
  useLiveIndustryGroupsQuery(options, {
    immediate: computed(() => !isEmpty(symbol.value)),
  });

const { offersIsin, initialPublicOffers } = useLiveInitialPublicOffers();

const handleBuy = (row: InstrumentSectorDetail) => {
  const offer = initialPublicOffers.value?.find(
    (offer) => offer.instrumentIsin === row.isin
  );

  if (offer) {
    openBuyOfferDialog({ offer });
  } else {
    openNewOrderFormDialog({
      type: ORDER_TYPES.buy,
      symbol: {
        instrumentName: row.name as string,
        isin: getSymbolIsin(row),
      },
    });
  }
};

const retryIndustryGroups = async () => {
  if (isErrorSymbol.value) {
    await getSymbol();
  }

  await getIndustryGroupItems();
};
</script>
